/**
 * Rapor geçmişi — yalnızca localStorage.
 *
 * Her kayıt minimaldir: skorlar, kategoriler ve issue başlıkları. Ham belge
 * metni saklanmaz. localStorage erişilemezse veya kota dolarsa sessizce
 * devam edilir; en eski kayıtlar silinir.
 */
import type { Report } from '#lib/types';
import { issueTextEn } from '#lib/i18n/issues';
import type { HistoryIssue, HistorySnapshot } from './types';

export const HISTORY_KEY = 'repcheck:history';
export const MAX_HISTORY = 20;

function storage(): Storage | null {
	try {
		if (typeof localStorage === 'undefined') return null;
		return localStorage;
	} catch {
		return null;
	}
}

export function makeSnapshot(report: Report): HistorySnapshot {
	const issues: HistoryIssue[] = report.issues.map((issue) => ({
		code: issue.code,
		category: issue.category,
		severity: issue.severity,
		titleTr: issue.title,
		titleEn: issueTextEn(issue)?.title ?? issue.title,
		confidence: issue.confidence,
		decisive: issue.decisive,
		page: issue.location.page,
		line: issue.location.sourceLine,
		charStart: issue.location.charStart
	}));

	return {
		id: makeId(),
		fileName: report.document.fileName,
		date: report.generatedAt,
		score: report.scores.overall,
		grade: report.scores.grade,
		issueCount: issues.length,
		counts: report.counts,
		categories: report.scores.categories.map((c) => ({
			category: c.category,
			score: c.score,
			weight: c.weight
		})),
		issues
	};
}

function makeId(): string {
	try {
		if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
	} catch {
		/* ignore */
	}
	return `h-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function loadHistory(): HistorySnapshot[] {
	const store = storage();
	if (!store) return [];
	try {
		const raw = store.getItem(HISTORY_KEY);
		if (!raw) return [];
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) return [];
		return parsed.filter(isSnapshot);
	} catch {
		return [];
	}
}

function isSnapshot(value: unknown): value is HistorySnapshot {
	if (!value || typeof value !== 'object') return false;
	const v = value as Partial<HistorySnapshot>;
	return (
		typeof v.id === 'string' &&
		typeof v.fileName === 'string' &&
		typeof v.score === 'number' &&
		typeof v.grade === 'string' &&
		Array.isArray(v.issues)
	);
}

/** Kaydeder; başarısız olsa bile hatayı yutmaz, false döner. */
export function saveSnapshot(snapshot: HistorySnapshot): boolean {
	const store = storage();
	if (!store) return false;
	let list = loadHistory().filter((s) => s.id !== snapshot.id);
	list.unshift(snapshot);
	list = list.slice(0, MAX_HISTORY);

	for (let attempt = 0; attempt < 6; attempt++) {
		try {
			store.setItem(HISTORY_KEY, JSON.stringify(list));
			return true;
		} catch {
			if (list.length <= 1) {
				try {
					store.removeItem(HISTORY_KEY);
				} catch {
					/* ignore */
				}
				return false;
			}
			list = list.slice(0, list.length - 1);
		}
	}
	return false;
}

export function clearHistory(): void {
	const store = storage();
	if (!store) return;
	try {
		store.removeItem(HISTORY_KEY);
	} catch {
		/* ignore */
	}
}
